import React, { useState } from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import moment from 'moment/moment';
import { useTranslation } from 'react-i18next';

import { DateInput } from 'shared/ui';

import { FieldErrors } from '../../entities/modal/model/types/modalFieldErrors';
import { TModalMode } from '../../shared/model/types/modalMode.type';
import { useAppDispatch } from '../../store';
import { changeModalDate } from '../../widgets/Modals/model/slice/addModalSlice';
import { changeEditModalEndDate } from '../../widgets/Modals/model/slice/editModalSlice';

dayjs.extend(customParseFormat);

const MIN_DATE = dayjs('2020-01-01');
const MAX_DATE = dayjs().add(2, 'year').endOf('year');
const DATE_FORMATS = ['DD.MM.YYYY', 'DD.MM.YY', 'D.M.YYYY', 'D.M.YY'];

interface IModalDatePicker {
    initialDate?: string;
    modalMode: TModalMode;
    isDisabled?: boolean;
    isCheckedRadio: boolean;
    isModalLoading: boolean;
    fieldErrors: FieldErrors;
    setFieldErrors: React.Dispatch<React.SetStateAction<FieldErrors>>;
}

export const ModalDatePicker = ({
    initialDate = '',
    modalMode,
    isDisabled = false,
    isCheckedRadio,
    isModalLoading,
    fieldErrors,
    setFieldErrors,
}: IModalDatePicker) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [inputValue, setInputValue] = useState(initialDate);

    const onDateSubmit = (value: string) => {
        if (isDisabled) return;
        setFieldErrors((prev) => ({
            ...prev,
            date: {
                isError: false,
                errorMessage: '',
            },
        }));

        if (!value.length) {
            modalMode === 'addNewTask' && dispatch(changeModalDate(''));
            modalMode === 'editTask' && dispatch(changeEditModalEndDate(''));
            return;
        }
        if (value === '0') {
            modalMode === 'addNewTask' && dispatch(changeModalDate(null));
            modalMode === 'editTask' && dispatch(changeEditModalEndDate(null));
            return;
        }
        const parsedDate = moment(value, 'DD.MM.YYYY', true);
        // console.log('parsedDate: ', parsedDate.isValid());
        if (parsedDate.isValid()) {
            modalMode === 'addNewTask' && dispatch(changeModalDate(value));
            modalMode === 'editTask' && dispatch(changeEditModalEndDate(value));
        } else {
            setFieldErrors((prev) => ({
                ...prev,
                date: {
                    isError: true,
                    errorMessage: t('Features.ModalDatePicker.InvalidData.text'),
                },
            }));
        }
    };

    const handleChangeDate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = e.target.value;
        const previousValue = inputValue || '';

        const filteredValue = rawValue
            .replace(/[^\d.]/g, '')
            .replace(/\.+/g, '.')
            .slice(0, 10);

        const isDeletion = rawValue.length < previousValue.length;

        const addAutoDots = (value: string) => {
            if (isDeletion) return value;

            const parts = value.split('.');
            let newValue = value;

            if (parts[0]?.length === 2 && parts.length === 1) newValue += '.';
            if (parts[1]?.length === 2 && parts.length === 2) newValue += '.';

            return newValue.slice(0, 10);
        };

        const formattedValue = addAutoDots(filteredValue);
        const isCompleteDate = formattedValue.split('.').length === 3;

        const parsedDate = dayjs(formattedValue, DATE_FORMATS, true);

        let errorMessage = '';
        let shouldDispatch = false;

        if (formattedValue) {
            if (!parsedDate.isValid()) {
                errorMessage = t('Features.ModalDatePicker.InvalidData.text');
            } else if (isCompleteDate) {
                if (parsedDate.isBefore(MIN_DATE)) {
                    errorMessage = `${t(
                        'Features.ModalDatePicker.InvalidData.early.text'
                    )} ${MIN_DATE.format('DD.MM.YYYY')}`;
                } else if (parsedDate.isAfter(MAX_DATE)) {
                    errorMessage = `${t(
                        'Features.ModalDatePicker.InvalidData.later.text'
                    )} ${MAX_DATE.format('DD.MM.YYYY')}`;
                }
            }
        }

        if (isCompleteDate && parsedDate.isValid() && !errorMessage) {
            shouldDispatch = true;
        }

        setInputValue(formattedValue);
        setFieldErrors((prev) => ({
            ...prev,
            date: {
                isError: !!errorMessage,
                errorMessage: errorMessage || null,
            },
        }));

        if (shouldDispatch) {
            const normalizedDate = parsedDate.format('DD.MM.YYYY');
            modalMode === 'addNewTask' && dispatch(changeModalDate(normalizedDate));
            modalMode === 'editTask' && dispatch(changeEditModalEndDate(normalizedDate));
        } else {
            modalMode === 'addNewTask' && dispatch(changeModalDate(''));
            modalMode === 'editTask' && dispatch(changeEditModalEndDate(''));
        }
    };

    return (
        <DateInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            placeholder={t('Features.ModalDatePicker.DateInput.placeholder.text')}
            parameterName="date"
            newCallback={onDateSubmit}
            options={{
                // startDate: moment(modalData.date).format('DD.MM.YYYY'),
                autoUpdateInput: false,
                singleDatePicker: true,
                showDropdowns: true,
                autoApply: true,
            }}
            isDisabled={isCheckedRadio || isModalLoading || isDisabled}
            error={fieldErrors.date}
            onDateChange={handleChangeDate}
        />
    );
};
